import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { QrCode, Copy, ExternalLink, X, CheckCircle, Clock, AlertCircle, MessageCircle, Timer } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import CPFModal from "./CPFModal";
import { PIX_EXPIRACAO_PADRAO_MIN, segundosAteExpirar } from "./pixExpiracao";

const API = '/api';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
// Só até o PIX ser criado: daí em diante vale o expires_at do servidor (achado 222)
const PIX_EXPIRATION_MINUTES = PIX_EXPIRACAO_PADRAO_MIN;

export default function PixOnlineModal({
  open,
  onClose,
  orderId,
  customerId,
  amount,
  onPaymentConfirmed
}) {
  const [pixData, setPixData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('pending');
  const [showCPFModal, setShowCPFModal] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(PIX_EXPIRATION_MINUTES * 60); // segundos
  const [isExpired, setIsExpired] = useState(false);
  const pollingIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const hasCreatedPixRef = useRef(false);

  // Cleanup do polling e timer quando componente desmonta ou modal fecha
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  // Reset quando modal fecha
  useEffect(() => {
    if (!open) {
      hasCreatedPixRef.current = false;
      setPixData(null);
      setStatus('pending');
      setWhatsappSent(false);
      setTimeLeft(PIX_EXPIRATION_MINUTES * 60);
      setIsExpired(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  }, [open]);

  // Timer countdown
  useEffect(() => {
    if (pixData && status === 'pending' && !isExpired) {
      timerIntervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
            setIsExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };
    }
  }, [pixData, status, isExpired]);

  // Formatar tempo restante
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Cor do timer baseado no tempo restante (usa cores do sistema via --primary)
  const getTimerColor = () => {
    if (timeLeft <= 60) return 'text-red-600 bg-red-50 border-red-200'; // Último minuto
    if (timeLeft <= 180) return 'text-orange-600 bg-orange-50 border-orange-200'; // Últimos 3 minutos
    return 'text-orange-600 bg-orange-50 border-orange-500/40'; // Usa cor primária do sistema
  };

  // Criar PIX ao abrir modal
  useEffect(() => {
    if (open && orderId && !hasCreatedPixRef.current && !pixData) {
      hasCreatedPixRef.current = true;
      createPixPayment();
    }
  }, [open, orderId]);

  const createPixPayment = async () => {
    if (!orderId) {
      toast.error("ID do pedido não encontrado");
      return;
    }

    setLoading(true);
    try {
      // Endpoint público - não precisa de autenticação
      const response = await axios.post(
        `${API}/payments/pix-online/create`,
        { nucleo_order_id: orderId },
        { timeout: 30000 } // 30 segundos de timeout
      );
      
      if (!response.data) {
        throw new Error("Resposta inválida do servidor");
      }

      // Cronômetro pelo prazo configurado na loja, contado da criação: o
      // mesmo PIX pendente reaberto continua de onde estava.
      const restante = segundosAteExpirar(response.data.expires_at);
      setTimeLeft(restante);
      setIsExpired(restante <= 0);
      setPixData(response.data);
      setStatus(response.data.status || 'pending');
      setWhatsappSent(response.data.whatsapp_sent || false);

      // Iniciar polling se ainda estiver pendente
      if (response.data.status === 'pending' || response.data.status === 'in_process') {
        startPolling(orderId, restante);
      } else if (response.data.status === 'approved') {
        toast.success("Pagamento já foi confirmado! ✅");
        if (onPaymentConfirmed) {
          onPaymentConfirmed();
        }
      }
      
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error("Tempo de espera esgotado. Tente novamente.");
      } else if (error.response?.status === 409 && error.response?.headers?.['x-error-code'] === 'CPF_REQUIRED') {
        // CPF necessário - abrir modal
        setShowCPFModal(true);
      } else if (error.response?.status === 404) {
        toast.error("Pedido não encontrado");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || "Dados inválidos");
      } else if (error.response?.status >= 500) {
        toast.error("Erro no servidor. Tente novamente em alguns instantes.");
      } else {
        toast.error(error.response?.data?.detail || "Erro ao criar PIX. Tente novamente.");
      }
      
      // Não fechar modal se for erro de CPF
      if (!(error.response?.status === 409 && error.response?.headers?.['x-error-code'] === 'CPF_REQUIRED')) {
        hasCreatedPixRef.current = false; // Permitir tentar novamente
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCPFSaved = async () => {
    setShowCPFModal(false);
    // Resetar flag para permitir criar PIX novamente
    hasCreatedPixRef.current = false;
    // Tentar criar PIX novamente após salvar CPF
    await createPixPayment();
  };

  const startPolling = (orderId, segundosValidade = PIX_EXPIRATION_MINUTES * 60) => {
    // Limpar polling anterior se existir
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Polling a cada 5s enquanto o PIX vale (prazo da loja). Antes parava em 2 min:
    // cliente que pagava no minuto 4 nunca via a confirmacao na tela.
    const pollingIntervalSeconds = 5;
    const pollingInterval = pollingIntervalSeconds * 1000;
    const maxAttempts = Math.ceil(segundosValidade / pollingIntervalSeconds) + 6;
    
    let attempts = 0;
    
    const interval = setInterval(async () => {
      attempts++;
      
      // Verificar se modal ainda está aberto
      if (!open) {
        clearInterval(interval);
        pollingIntervalRef.current = null;
        return;
      }
      
      if (attempts > maxAttempts) {
        clearInterval(interval);
        pollingIntervalRef.current = null;
        toast.info("Tempo de verificação esgotado. Verifique o pagamento manualmente.");
        return;
      }
      
      try {
        // Endpoint público - não precisa de autenticação
        const response = await axios.get(
          `${API}/payments/pix-online/status/${orderId}`,
          { timeout: 10000 } // 10 segundos de timeout
        );
        
        if (!response.data) {
          return; // Resposta inválida, continuar tentando
        }

        const newStatus = response.data.status;
        setStatus(newStatus);
        
        if (newStatus === 'approved') {
          clearInterval(interval);
          pollingIntervalRef.current = null;
          toast.success("Pagamento confirmado! ✅");
          if (onPaymentConfirmed) {
            onPaymentConfirmed();
          }
          setTimeout(() => {
            onClose();
          }, 2000);
        } else if (newStatus === 'expired' || newStatus === 'rejected' || newStatus === 'cancelled') {
          clearInterval(interval);
          pollingIntervalRef.current = null;
          toast.error("Pagamento não foi confirmado. Tente gerar um novo PIX.");
        }
        
      } catch (error) {
        // Não fazer nada em caso de erro de rede, apenas continuar tentando
        // Log apenas em desenvolvimento
        if (IS_DEVELOPMENT) {
          console.warn("Erro ao verificar status do PIX:", error);
        }
      }
    }, pollingInterval);
    
    pollingIntervalRef.current = interval;
  };

  const copyPixCode = async () => {
    if (pixData?.qr_code) {
      try {
        await navigator.clipboard.writeText(pixData.qr_code);
        toast.success("Código PIX copiado! 📋");
      } catch (err) {
        // Fallback para navegadores antigos
        const textArea = document.createElement("textarea");
        textArea.value = pixData.qr_code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success("Código PIX copiado! 📋");
      }
    }
  };

  const openTicketUrl = () => {
    if (pixData?.ticket_url) {
      window.open(pixData.ticket_url, '_blank');
    }
  };

  const openPublicUrl = () => {
    if (pixData?.public_url) {
      window.open(pixData.public_url, '_blank');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent hideClose className="w-[95vw] sm:max-w-md p-0 gap-0">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <QrCode className="w-6 h-6 text-orange-500" />
              PIX Online
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-0">
            <div className="flex items-center justify-end mb-4 -mt-4">
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loading && !pixData && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Gerando código PIX...</p>
              </div>
            )}

            {pixData && (
              <>
                {/* Status */}
                <div className={`p-4 rounded-lg mb-4 flex items-center gap-3 ${
                  status === 'approved'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : status === 'pending' || status === 'in_process'
                    ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {status === 'approved' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : status === 'pending' || status === 'in_process' ? (
                    <Clock className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <div>
                    <p className="font-semibold">
                      {status === 'approved'
                        ? 'Pagamento Aprovado!'
                        : status === 'pending' || status === 'in_process'
                        ? 'Aguardando pagamento...'
                        : 'Pagamento não confirmado'}
                    </p>
                    {status === 'pending' || status === 'in_process' ? (
                      <p className="text-sm mt-1">Verificando automaticamente...</p>
                    ) : null}
                  </div>
                </div>

                {/* WhatsApp enviado */}
                {whatsappSent && (
                  <div className="p-3 rounded-lg mb-4 flex items-center gap-3 bg-green-50 text-green-700 border border-green-200">
                    <MessageCircle className="w-5 h-5" />
                    <p className="text-sm font-medium">
                      Chave PIX enviada no seu WhatsApp!
                    </p>
                  </div>
                )}

                {/* Timer de expiração */}
                {(status === 'pending' || status === 'in_process') && (
                  <div className={`p-3 rounded-lg mb-4 flex items-center justify-center gap-3 border ${getTimerColor()}`}>
                    <Timer className="w-5 h-5" />
                    <div className="text-center">
                      <p className="text-xs font-medium uppercase tracking-wide opacity-75">
                        {isExpired ? 'PIX Expirado' : 'Tempo restante'}
                      </p>
                      <p className="text-2xl font-bold font-mono">
                        {isExpired ? '00:00' : formatTime(timeLeft)}
                      </p>
                    </div>
                  </div>
                )}

                {/* QR Code */}
                {pixData.qr_code_base64 && !isExpired && (
                  <div className="bg-white p-4 rounded-lg mb-4 text-center border-2 border-gray-200 shadow-sm">
                    <img
                      src={`data:image/jpeg;base64,${pixData.qr_code_base64}`}
                      alt="QR Code PIX"
                      className="mx-auto max-w-full h-auto rounded-lg"
                      style={{ maxWidth: '200px' }}
                    />
                  </div>
                )}

                {/* Mensagem de expirado */}
                {isExpired && (
                  <div className="p-4 rounded-lg mb-4 bg-red-50 border border-red-200 text-center">
                    <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="font-semibold text-red-700">PIX Expirado</p>
                    <p className="text-sm text-red-600 mt-1">Feche este modal e gere um novo PIX</p>
                  </div>
                )}

                {/* Código Copia e Cola */}
                {pixData.qr_code && !isExpired && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Código PIX (Copia e Cola)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={pixData.qr_code}
                        readOnly
                        className="flex-1 px-3 h-12 border-2 border-gray-300 rounded-lg text-sm font-mono bg-white text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30"
                      />
                      <Button
                        onClick={copyPixCode}
                        className="px-5 h-12 bg-orange-500 hover:bg-orange-600 text-white shadow-md hover:shadow-lg transition-all"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copiar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Botões de ação */}
                <div className="space-y-2">
                  {pixData.public_url && (
                    <Button
                      onClick={openPublicUrl}
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Abrir página de pagamento
                    </Button>
                  )}

                  {pixData.ticket_url && (
                    <Button
                      onClick={openTicketUrl}
                      variant="outline"
                      className="w-full h-12"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Abrir no Mercado Pago
                    </Button>
                  )}
                </div>

                {/* Valor */}
                {amount && amount > 0 && (
                  <div className="mt-4 pt-4 border-t text-center">
                    <p className="text-sm text-gray-600">Valor a pagar</p>
                    <p className="text-2xl font-bold text-orange-500">
                      R$ {Number(amount).toFixed(2).replace('.', ',')}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de CPF */}
      <CPFModal
        open={showCPFModal}
        onClose={() => setShowCPFModal(false)}
        customerId={customerId}
        onSaved={handleCPFSaved}
      />
    </>
  );
}
